// src/components/ProtectedRoute.jsx
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { refreshAccessToken } from "../Utils/authHelpers";

import { API_BASE_URL } from "../config/constants";

function ProtectedRoute({ children, requireAdmin = false }) {
  const [isValid, setIsValid] = useState(null); // null: loading, true: valid, false: invalid
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();

  // Decode JWT payload
  const decodeJwtPayload = (token) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  };

  // Check if token is expired
  const isTokenExpired = (token) => {
    const payload = decodeJwtPayload(token);
    if (!payload?.exp) return true;
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  };

  // Validate token via API
  const validateToken = async (token) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      return response.data?.userRole || null;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        setIsValid(false);
        return;
      }

      if (isTokenExpired(token)) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          const payload = decodeJwtPayload(newToken);
          const role = payload?.userRole || payload?.role || null;
          setUserRole(role);
          setIsValid(true);
        } else {
          setIsValid(false);
        }
        return;
      }

      // Extract role from JWT claims directly — avoids an extra API call
      const payload = decodeJwtPayload(token);
      const role = payload?.userRole || payload?.role || null;
      if (role) {
        setUserRole(role);
        setIsValid(true);
        return;
      }

      // Fallback: validate via API if JWT has no role claim
      const apiRole = await validateToken(token);
      setUserRole(apiRole);
      setIsValid(!!apiRole);
    };

    checkAuth();
  }, []);

  if (isValid === null) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <div style={{ textAlign: "center", color: "#666" }}>
          <div style={{ width: 32, height: 32, border: "3px solid #e0e0e0", borderTopColor: "#d4af37", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
          <p style={{ fontSize: 14, fontFamily: "system-ui, sans-serif" }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "70vh", padding: "40px 20px" }}>
        <div style={{ textAlign: "center", maxWidth: 360 }}>
          <p style={{ fontFamily: '"Caslon Antique", "Cormorant Garamond", Georgia, serif', fontSize: 13, letterSpacing: "0.15em", color: "#888", marginBottom: 20 }}>
            A M I N I I
          </p>
          <h2 style={{ fontFamily: '"Caslon Antique", "Cormorant Garamond", Georgia, serif', fontSize: 22, fontWeight: "normal", marginBottom: 10, color: "#1a1a1a", letterSpacing: "0.03em" }}>
            Login to Continue
          </h2>
          <p style={{ color: "#888", fontSize: 13, marginBottom: 32, fontFamily: '"Poppins", system-ui, sans-serif', letterSpacing: "0.02em" }}>
            This page is for logged-in members only.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => navigate("/login", { state: { from: window.location.pathname } })}
              style={{ background: "#1a1a1a", color: "#fff", border: "none", padding: "13px 32px", fontSize: 13, cursor: "pointer", fontFamily: '"Poppins", system-ui, sans-serif', letterSpacing: "0.05em", minWidth: 110 }}
            >
              Login
            </button>
            <button
              onClick={() => navigate("/")}
              style={{ background: "transparent", color: "#1a1a1a", border: "1px solid #1a1a1a", padding: "13px 32px", fontSize: 13, cursor: "pointer", fontFamily: '"Poppins", system-ui, sans-serif', letterSpacing: "0.05em", minWidth: 110 }}
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (requireAdmin && !userRole?.toLowerCase().includes("admin")) {
    return <Navigate to="/" replace />;
  }

  return children;
}

export default ProtectedRoute;
